<template>
  <div class="feedback-widget" v-if="isAuthenticated">
    <!-- Floating button -->
    <button 
      v-if="!isOpen && canSubmit" 
      class="feedback-button"
      @click="openModal"
      title="Share your feedback"
    >
      ⭐
    </button>

    <!-- Modal -->
    <Teleport to="body">
      <div v-if="isOpen" class="feedback-overlay" @click.self="closeModal">
        <div class="feedback-modal">
          <button class="close-button" @click="closeModal">×</button>
          
          <div v-if="!submitted" class="feedback-content">
            <h2>How's your experience?</h2>
            <p class="subtitle">Your feedback helps us improve IQ</p>
            
            <!-- Star Rating -->
            <div class="star-rating">
              <button 
                v-for="star in 5" 
                :key="star"
                class="star"
                :class="{ 
                  active: star <= (hoverRating || rating),
                  hovered: star <= hoverRating && hoverRating !== rating
                }"
                @click="setRating(star)"
                @mouseenter="hoverRating = star"
                @mouseleave="hoverRating = 0"
              >
                {{ star <= (hoverRating || rating) ? '★' : '☆' }}
              </button>
            </div>
            <p class="rating-label" v-if="rating">{{ ratingLabel }}</p>
            
            <!-- Comment (optional) -->
            <div class="comment-section" v-if="rating">
              <label for="feedback-comment">Any additional thoughts? (optional)</label>
              <textarea 
                id="feedback-comment"
                v-model="comment"
                placeholder="Tell us what you think..."
                rows="3"
                maxlength="1000"
              ></textarea>
            </div>
            
            <!-- Submit -->
            <button 
              v-if="rating"
              class="submit-button"
              :disabled="submitting"
              @click="submitFeedback"
            >
              {{ submitting ? 'Submitting...' : 'Submit Feedback' }}
            </button>
            
            <p v-if="error" class="error-message">{{ error }}</p>
          </div>
          
          <!-- Thank you state -->
          <div v-else class="thank-you">
            <div class="checkmark">✓</div>
            <h2>Thank you!</h2>
            <p>Your feedback has been submitted.</p>
            <button class="close-final-button" @click="closeModal">Close</button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { logger } from '@/services/logger'

const authStore = useAuthStore()

// State
const isOpen = ref(false)
const rating = ref(0)
const hoverRating = ref(0)
const comment = ref('')
const submitting = ref(false)
const submitted = ref(false)
const error = ref('')
const canSubmit = ref(true)

// Computed
const isAuthenticated = computed(() => authStore.isAuthenticated)

const ratingLabel = computed(() => {
  const labels: Record<number, string> = {
    1: 'Poor',
    2: 'Fair',
    3: 'Good',
    4: 'Very Good',
    5: 'Excellent'
  }
  return labels[rating.value] || ''
})

// Owl API URL - configurable via env or default
const OWL_API_URL = import.meta.env.VITE_OWL_API_URL || 'https://owl.incentit.com'
const COOLDOWN_DAYS = 30
const STORAGE_KEY = 'iq-feedback-last-submitted'

// Detect environment from hostname
function getEnvironment(): string {
  const hostname = window.location.hostname
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) return 'local'
  if (hostname.includes('qaiq')) return 'qa'
  if (hostname.includes('caniq')) return 'canada'
  if (hostname.includes('iq.incentit')) return 'production'
  return 'unknown'
}

// Methods
function openModal() {
  isOpen.value = true
  rating.value = 0
  hoverRating.value = 0
  comment.value = ''
  submitted.value = false
  error.value = ''
}

function closeModal() {
  isOpen.value = false
}

function setRating(value: number) {
  rating.value = value
}

async function checkCanSubmit() {
  // First check localStorage for quick feedback
  const lastSubmitted = localStorage.getItem(STORAGE_KEY)
  if (lastSubmitted) {
    const daysSince = (Date.now() - parseInt(lastSubmitted, 10)) / (1000 * 60 * 60 * 24)
    if (daysSince < COOLDOWN_DAYS) {
      canSubmit.value = false
      return
    }
  }
  
  // Then verify with server (in case localStorage was cleared)
  try {
    const userId = authStore.user?.userId
    if (!userId) return
    
    const response = await fetch(
      `${OWL_API_URL}/api/feedback/can-submit?source=iq&userId=${encodeURIComponent(userId)}`
    )
    if (response.ok) {
      const data = await response.json()
      canSubmit.value = data.canSubmit
      
      // Sync localStorage with server
      if (!data.canSubmit && data.lastSubmittedAt) {
        localStorage.setItem(STORAGE_KEY, new Date(data.lastSubmittedAt).getTime().toString())
      }
    }
  } catch (err) {
    // If check fails, allow submission (server will enforce cooldown)
    logger.warn('Failed to check feedback cooldown', { error: err })
  }
}

async function submitFeedback() {
  if (!rating.value || submitting.value) return
  
  submitting.value = true
  error.value = ''
  
  try {
    const payload = {
      source: 'iq',
      rating: rating.value,
      comment: comment.value.trim() || null,
      context: window.location.pathname,
      environment: getEnvironment(),
      userId: authStore.user?.userId || null,
      userEmail: authStore.user?.email || null,
      userName: authStore.user?.userName || null,
      tenantId: authStore.user?.tenantId || null,
      tenantName: authStore.user?.tenantName || null,
      userAgent: navigator.userAgent
    }
    
    logger.info('Submitting feedback', { rating: payload.rating, hasComment: !!payload.comment })
    
    const response = await fetch(`${OWL_API_URL}/api/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    
    const data = await response.json()
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to submit feedback')
    }
    
    // Success
    submitted.value = true
    canSubmit.value = false
    localStorage.setItem(STORAGE_KEY, Date.now().toString())
    
    logger.info('Feedback submitted successfully', { id: data.id })
    
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Something went wrong'
    error.value = message
    logger.error('Failed to submit feedback', { error: err })
  } finally {
    submitting.value = false
  }
}

// Check cooldown on mount
onMounted(() => {
  if (isAuthenticated.value) {
    checkCanSubmit()
  }
})
</script>

<style scoped>
.feedback-button {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  font-size: 24px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  transition: transform 0.2s, box-shadow 0.2s;
  z-index: 1000;
}

.feedback-button:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.5);
}

.feedback-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.feedback-modal {
  background: white;
  border-radius: 16px;
  padding: 32px;
  width: 90%;
  max-width: 420px;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalIn 0.25s ease-out;
}

@keyframes modalIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.close-button {
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  font-size: 28px;
  color: #999;
  cursor: pointer;
  line-height: 1;
  padding: 4px 8px;
}

.close-button:hover {
  color: #333;
}

.feedback-content h2 {
  margin: 0 0 8px;
  font-size: 1.5rem;
  color: #333;
  text-align: center;
}

.subtitle {
  margin: 0 0 24px;
  color: #666;
  text-align: center;
  font-size: 0.95rem;
}

.star-rating {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 8px;
}

.star {
  background: none;
  border: none;
  font-size: 40px;
  cursor: pointer;
  color: #999;
  transition: transform 0.15s, color 0.15s;
  padding: 4px;
}

.star.active {
  color: #f6ad55;
}

.star.hovered {
  color: #fbd38d;
}

.star:hover {
  transform: scale(1.15);
}

.rating-label {
  text-align: center;
  color: #667eea;
  font-weight: 600;
  margin-bottom: 20px;
}

.comment-section {
  margin-bottom: 20px;
}

.comment-section label {
  display: block;
  margin-bottom: 8px;
  font-size: 0.9rem;
  color: #555;
}

.comment-section textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.95rem;
  resize: vertical;
  min-height: 80px;
}

.comment-section textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.submit-button {
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.1s;
}

.submit-button:hover:not(:disabled) {
  opacity: 0.9;
}

.submit-button:active:not(:disabled) {
  transform: scale(0.98);
}

.submit-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.error-message {
  color: #e53e3e;
  text-align: center;
  margin-top: 12px;
  font-size: 0.9rem;
}

.thank-you {
  text-align: center;
  padding: 20px 0;
}

.checkmark {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: white;
  margin: 0 auto 20px;
}

.thank-you h2 {
  margin: 0 0 8px;
  color: #333;
}

.thank-you p {
  color: #666;
  margin-bottom: 24px;
}

.close-final-button {
  padding: 12px 32px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
}

.close-final-button:hover {
  background: #5a67d8;
}

/* Responsive */
@media (max-width: 480px) {
  .feedback-modal {
    padding: 24px;
    margin: 16px;
  }
  
  .star {
    font-size: 32px;
  }
  
  .feedback-button {
    bottom: 16px;
    right: 16px;
    width: 48px;
    height: 48px;
    font-size: 20px;
  }
}
</style>
